/**
 * File view component.
 * @module components/theme/View/FileView
 */

import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers';
import config from '@plone/volto/registry';

import PdfView from './PdfView.jsx';
import VideoView from './VideoView.jsx';
import ImageView from '@plone/volto/components/theme/View/ImageView.jsx';

import downloadIcon from './download.svg';

const FileView = ({ content }) => {
  const Container = config.getComponent({ name: 'Container' }).component || (
    <div className="ui container"></div>
  );

  const contentType = content.file?.['content-type'];

  let contentView;

  switch (contentType) {
    // PDFs
    case 'application/pdf':
      contentView = <PdfView content={content} />;
      break;

    // Images
    case 'image/jpeg':
    case 'image/png':
    case 'image/gif':
    case 'image/webp':
    case 'image/svg+xml':
      contentView = <ImageView content={content} />;
      break;

    // Videos
    case 'video/mp4':
    case 'video/webm':
    case 'video/ogg':
      contentView = <VideoView content={content} />;
      break;

    // Office / documents
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': // xlsx
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // docx
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation': // pptx
    case 'text/plain':
    case 'text/csv':
    case 'application/zip':
      // No special renderer yet
      contentView = (
        <>
          {content.description && (
            <p className="documentDescription">{content.description}</p>
          )}
        </>
      );
      break;

    default:
      contentView = (
        <>
          {content.description && (
            <p className="documentDescription">{content.description}</p>
          )}
        </>
      );
      break;
  }

  return (
    <Container className="file-view-wrapper">
      <h2>
        {content.title}
        {content.subtitle && ` - ${content.subtitle}`}
      </h2>

      {contentView}

      {content.file?.download && (
        <a href={flattenToAppURL(content.file.download)}>
          <span
            className="download-icon"
            style={{ backgroundImage: `url(${downloadIcon})` }}
          />
          {content.file.filename}
        </a>
      )}
    </Container>
  );
};

FileView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    file: PropTypes.shape({
      download: PropTypes.string,
      filename: PropTypes.string,
      'content-type': PropTypes.string,
    }),
  }).isRequired,
};

export default FileView;
